/**
 * Component: DineAIImpactDashboard
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import { motion } from 'framer-motion';
import { formatCurrency, formatNumber } from '@/utils/formatting';
import type { AnalyticsSummary, DateRange } from '@/types/analytics';

interface DineAIImpactDashboardProps {
  summary: AnalyticsSummary;
  dateRange: DateRange;
  isHistorical?: boolean;
  dataNotice?: string;
  loading?: boolean;
}

export function DineAIImpactDashboard({
  summary,
  dateRange,
  isHistorical,
  dataNotice,
  loading = false
}: DineAIImpactDashboardProps) {

  if (loading) {
    return (
      <div className="mb-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-lg p-4 h-24"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isEmpty = summary.total_orders === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
    >
      {/* Header with title and subtitle */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          Performance Overview
        </h2>
        {!isEmpty && (
          <p className="text-sm text-gray-500 mt-1">
            Your AI partner saved you {summary.staff_hours_saved} hours this period 🎉
          </p>
        )}
      </div>

      {/* Empty State */}
      {isEmpty && (
        <div className="text-center mb-6 bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to DINE AI Analytics
          </h2>
          <p className="text-xl text-green-600 mb-1">
            Your analytics dashboard is ready
          </p>
          <p className="text-gray-500">
            Once you start receiving orders, detailed analytics will appear here
          </p>
        </div>
      )}


      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Revenue Generated */}
        <motion.div
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(summary.revenue_generated)}
          </p>
          <p className="text-gray-500 text-sm">Revenue Generated</p>
        </motion.div>

        {/* Orders Captured */}
        <motion.div
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <p className="text-3xl font-bold text-gray-900">
            {formatNumber(summary.total_orders)}
          </p>
          <p className="text-gray-500 text-sm">Orders Captured</p>
        </motion.div>

        {/* Avg Order Value */}
        <motion.div
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(summary.avg_order_value)}
          </p>
          <p className="text-gray-500 text-sm">Avg Order Value</p>
        </motion.div>

        {/* Staff Hours Saved */}
        <motion.div
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <p className="text-3xl font-bold text-gray-900">
            {summary.staff_hours_saved}
          </p>
          <p className="text-gray-500 text-sm">Staff Hours Saved</p>
        </motion.div>
      </div>
    </motion.div>
  );
}
